import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tryParseMoney } from '@sitzmark/core';
import { useEffect, useReducer, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  Explain,
  Loading,
  Modal,
  Mono,
  More,
  Screen,
  Stat,
} from '../../components/ui.js';
import { useNotice } from '../../components/notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import { AddVariantsDialog } from '../products/style-editor.js';
import { AddVendorDialog } from './add-vendor.js';
import {
  createBlockedReason,
  emptyOrderDraft,
  orderDraftReducer,
  orderStyles,
  orderTotals,
  suggestedQuantity,
  toCreateInput,
} from './order-draft.js';
import type { OrderStyle, OrderableVariant } from './order-draft.js';
import { sizeGrids } from './size-grid.js';
import './receiving.css';
import { DateInput } from '../../components/date-input.js';

/**
 * Writing a purchase order by hand (SCOPE §6, §5.2 — a P0 in §4).
 *
 * The client's requirement, verbatim: *"Writing purchase orders: Either pull
 * items from existing inventory or build new. By - grid or sku."* Until now an
 * order could only be born from a vendor file (§5), which is the flagship path
 * and covers a season's buy — but a phone reorder of last season's gloves has
 * no spreadsheet, and neither does a rep writing on a pad.
 *
 * **One path, not two.** "Grid or SKU" is not a mode to choose here any more
 * than it is when creating a style (§5.2): find the style, and its colour ×
 * size grid is however big it is. A tub of wax is a grid with one cell. A mode
 * switch would be a decision point that buys nothing (§2.2-2), and the grid is
 * the same `.matrix` a buyer already knows from Products (§2.2-4).
 *
 * **Vendor first, because it scopes everything after it** — the style search is
 * filtered to that supplier by default, which is what makes finding an item one
 * keystroke instead of a scroll (§2.2-5).
 *
 * Step budget: **≤5 to a one-line order**, asserted on the reducer in
 * `order-draft.test.ts`, not estimated here.
 */

type StyleRow = Awaited<ReturnType<typeof api.products.list.query>>[number];

export function NewOrderScreen({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (purchaseOrderId: string) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [draft, dispatch] = useReducer(orderDraftReducer, emptyOrderDraft);
  const [term, setTerm] = useState('');
  const [openStyleId, setOpenStyleId] = useState<string | null>(null);
  /** Typing anywhere on the order types into its item search (client, 2026-10-15). */
  const searchRef = useSearchTyping(setTerm, openStyleId === null);
  const [debounced, setDebounced] = useState('');
  const [addingVendor, setAddingVendor] = useState(false);
  const [allVendors, setAllVendors] = useState(false);
  const [creatingStyle, setCreatingStyle] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const styles = useQuery({
    queryKey: ['products', 'list', debounced, draft.vendorId, allVendors],
    enabled: draft.vendorId !== null,
    queryFn: () =>
      api.products.list.query({
        term: debounced,
        ...(allVendors || draft.vendorId === null ? {} : { vendorId: draft.vendorId }),
      }),
  });

  const style = useQuery({
    queryKey: ['products', 'get', openStyleId],
    enabled: openStyleId !== null,
    queryFn: () => api.products.get.query({ productId: openStyleId! }),
  });

  const create = useMutation({
    mutationFn: () => api.purchasing.createPurchaseOrder.mutate(toCreateInput(draft)),
    onSuccess: (result) => {
      recordBreadcrumb('purchasing', `wrote ${result.number} — ${result.units} units`);
      const vendor =
        taxonomy.data?.vendors.find((entry) => entry.id === draft.vendorId)?.name ?? null;
      notify({
        title: `${result.number} created — ${vendor === null ? '' : `${vendor}, `}${result.units} ${
          result.units === 1 ? 'unit' : 'units'
        }`,
      });
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
      onCreated(result.id);
    },
  });

  const totals = orderTotals(draft);
  const blocked = createBlockedReason(draft);
  const vendorName =
    taxonomy.data?.vendors.find((vendor) => vendor.id === draft.vendorId)?.name ?? null;

  return (
    <Screen
      title="New purchase order"
      subtitle="Pull items from stock, or build new ones."
      width="wide"
      context={
        totals.units > 0 ? (
          <div className="receiving__stats">
            <Stat label="Lines" value={totals.lines} />
            <Stat label="Units" value={totals.units} />
            <Stat
              label={
                <>
                  At cost{' '}
                  <Explain label="order cost">
                    Each line&rsquo;s quantity × its unit cost, summed. Costs default to what this
                    item cost last time — its weighted moving average — and are typed over when the
                    vendor has quoted something else.
                  </Explain>
                </>
              }
              value={money(totals.costCents)}
              emphasis
            />
          </div>
        ) : undefined
      }
      destructiveAction={
        <Button variant="ghost" onClick={onClose}>
          Discard
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={create.isPending}
          disabledReason={blocked ?? undefined}
          onClick={() => create.mutate()}
        >
          Create order{totals.units > 0 ? ` · ${totals.units} units` : ''}
        </Button>
      }
    >
      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}

      {addingVendor ? (
        <AddVendorDialog
          onClose={() => setAddingVendor(false)}
          onCreated={(vendor) => {
            setAddingVendor(false);
            setOpenStyleId(null);
            dispatch({ type: 'vendor-chosen', vendorId: vendor.id });
          }}
        />
      ) : null}

      <Card title="Who are you ordering from?">
        <div className="order__vendor">
          <div className="field">
            <label className="field__label" htmlFor="order-vendor">
              Vendor
            </label>
            {/* Searchable: 106 vendors is a scroll through the alphabet (2026-10-02). */}
            <SearchSelect
              id="order-vendor"
              placeholder="Pick a vendor — type to search"
              value={draft.vendorId ?? ''}
              options={(taxonomy.data?.vendors ?? []).map((vendor) => ({
                value: vendor.id,
                label: vendor.name,
              }))}
              onChange={(vendorId) => {
                if (vendorId === (draft.vendorId ?? '')) return;
                setOpenStyleId(null);
                dispatch({ type: 'vendor-chosen', vendorId });
              }}
            />
            {draft.lines.length > 0 ? (
              <p className="field__hint">
                Changing vendor clears the order — a purchase order belongs to one supplier.
              </p>
            ) : null}
            {/* Where the gap is noticed is where it gets fixed (§2.2-11). */}
            <Button variant="ghost" onClick={() => setAddingVendor(true)}>
              Add a vendor
            </Button>
          </div>
        </div>

        {/* Rare, so it sits behind one affordance (§2.2-2). */}
        <More label="Reference, dates, terms and notes">
          <div className="style-form">
            <div className="field">
              <label className="field__label" htmlFor="order-ref">
                Vendor&rsquo;s order number
              </label>
              <input
                id="order-ref"
                className="input"
                value={draft.vendorReference}
                onChange={(event) =>
                  dispatch({
                    type: 'detail-set',
                    field: 'vendorReference',
                    value: event.target.value,
                  })
                }
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="order-season">
                Season
              </label>
              <input
                id="order-season"
                className="input"
                placeholder="FA26"
                value={draft.season}
                onChange={(event) =>
                  dispatch({ type: 'detail-set', field: 'season', value: event.target.value })
                }
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="order-ship">
                Expected ship date
              </label>
              <DateInput
                id="order-ship"

                value={draft.shipDate}
                onChange={(value) =>
                  dispatch({ type: 'detail-set', field: 'shipDate', value: value })
                }
              />
            </div>
            {/* FB-1069: stored and printed as "Cancel after", and nothing set it. */}
            <div className="field">
              <label className="field__label" htmlFor="order-cancel">
                Cancel after
              </label>
              <DateInput
                id="order-cancel"

                value={draft.cancelDate}
                onChange={(value) =>
                  dispatch({ type: 'detail-set', field: 'cancelDate', value: value })
                }
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="order-terms">
                Payment terms
              </label>
              <input
                id="order-terms"
                className="input"
                placeholder="The vendor's own, when left blank"
                value={draft.terms}
                onChange={(event) =>
                  dispatch({ type: 'detail-set', field: 'terms', value: event.target.value })
                }
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="order-discount">
                Discount
              </label>
              <input
                id="order-discount"
                className="input"
                placeholder="8% if paid by Dec 10"
                value={draft.discountTerms}
                onChange={(event) =>
                  dispatch({
                    type: 'detail-set',
                    field: 'discountTerms',
                    value: event.target.value,
                  })
                }
              />
              <p className="field__hint">Printed for the vendor. Costs are not changed by it.</p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="order-notes">
                Notes for the vendor
              </label>
              <textarea
                id="order-notes"
                className="input"
                rows={2}
                value={draft.notes}
                onChange={(event) =>
                  dispatch({ type: 'detail-set', field: 'notes', value: event.target.value })
                }
              />
            </div>
          </div>
        </More>
      </Card>

      <div id="order-how-many" />
      {draft.vendorId === null ? (
        <Callout tone="info">
          Pick a vendor and the search below shows their models first, which is almost always what
          you are ordering.
        </Callout>
      ) : (
        <Card
          title={
            openStyleId === null
              ? `Find something to order${vendorName === null ? '' : ` from ${vendorName}`}`
              : 'How many?'
          }
          actions={
            openStyleId !== null ? (
              <Button variant="ghost" onClick={() => setOpenStyleId(null)}>
                ← Back to search
              </Button>
            ) : undefined
          }
        >
          {openStyleId === null ? (
            <>
              <input
                ref={searchRef}
                className="input search__input"
                value={term}
                autoFocus
                placeholder="Model #, name, brand, SKU or vendor part number"
                onChange={(event) => setTerm(event.target.value)}
              />

              <label className="order__scope">
                <input
                  type="checkbox"
                  checked={allVendors}
                  onChange={(event) => setAllVendors(event.target.checked)}
                />
                Search every vendor&rsquo;s models
              </label>

              {/* The round trip item 22 complained about, closed: a style the
                  vendor just added is created here, and its grid opens ready
                  to order. */}
              <div className="order__newstyle">
                <Button variant="ghost" onClick={() => setCreatingStyle(true)}>
                  New model for this order
                </Button>
              </div>

              {styles.isPending ? (
                <Loading label="Searching" />
              ) : (styles.data?.length ?? 0) === 0 ? (
                <p className="text-secondary">
                  Nothing matches. If the vendor just added it, press “New model for this order”.
                </p>
              ) : (
                <ul className="order__results">
                  {(styles.data ?? []).map((row: StyleRow) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="order__result"
                        onClick={() => setOpenStyleId(row.id)}
                      >
                        <span>
                          <strong>{row.name}</strong>{' '}
                          <span className="text-secondary">
                            <Mono>{row.styleNumber}</Mono>
                            {row.brand === null ? null : ` · ${row.brand}`}
                          </span>
                        </span>
                        <span className="text-secondary">
                          {row.variantCount} variants · {row.onHand} on hand
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : style.isPending ? (
            <Loading label="Loading model" />
          ) : style.data === undefined ? null : (
            <OrderGrid
              detail={style.data}
              quantityFor={(variantId) =>
                draft.lines.find((line) => line.variantId === variantId)?.quantity ?? 0
              }
              costFor={(variantId, fallback) =>
                draft.lines.find((line) => line.variantId === variantId)?.unitCostCents ?? fallback
              }
              onQuantity={(variant, quantity) =>
                dispatch({ type: 'quantity-set', variant, quantity })
              }
              onFill={(variants) => dispatch({ type: 'style-filled', variants })}
              onAdded={(updated) => {
                queryClient.setQueryData(['products', 'get', openStyleId], updated);
                void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
              }}
            />
          )}
        </Card>
      )}

      {creatingStyle && draft.vendorId !== null ? (
        <NewStyleForOrderDialog
          vendorId={draft.vendorId}
          taxonomy={taxonomy.data ?? null}
          onClose={() => setCreatingStyle(false)}
          onCreated={(productId) => {
            setCreatingStyle(false);
            void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
            setOpenStyleId(productId);
          }}
        />
      ) : null}

      {/*
        The order as grids, style by style (2026-10-02): "below the grid where
        it says on this order and the cost it shows items in a list instead of
        in a grid — ideally we could adjust cost via the grid." Each cell is
        its quantity and its unit cost; one box prices the whole style.
      */}
      {draft.lines.length > 0 ? (
        <Card title="On this order">
          {orderStyles(draft.lines).map((ordered) => (
            <OrderedStyle
              key={ordered.productId}
              style={ordered}
              onChange={() => {
                setOpenStyleId(ordered.productId);
                requestAnimationFrame(() =>
                  window.document
                    .getElementById('order-how-many')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                );
              }}
              onRemove={() => dispatch({ type: 'style-removed', productId: ordered.productId })}
              onCost={(variantId, unitCostCents) =>
                dispatch({ type: 'cost-set', variantId, unitCostCents })
              }
              onStyleCost={(unitCostCents) =>
                dispatch({ type: 'style-cost-set', productId: ordered.productId, unitCostCents })
              }
            />
          ))}
        </Card>
      ) : null}

      {/* The figures again, immediately above Create order — where the eye is
          when it decides (item 24). The header keeps its copy for while the
          grid is scrolled. */}
      {totals.units > 0 ? (
        <div className="order__totals">
          <span>
            {totals.lines} line{totals.lines === 1 ? '' : 's'}
          </span>
          <span>{totals.units} units</span>
          <strong className="money">{money(totals.costCents)} at cost</strong>
        </div>
      ) : null}
    </Screen>
  );
}

/**
 * A style that does not exist yet, created without leaving the order
 * (item 22). Name, number, filing and the two axes as lists — the same
 * colour × size multiplication the style editor does — and the grid opens
 * ready to order the moment it saves. The style itself is the same atomic
 * `products.create` every other path uses; anything the rep's pad did not
 * know (bins, targets, a description) lands in the finishing pass (item 39).
 */
export function NewStyleForOrderDialog({
  vendorId,
  taxonomy,
  onClose,
  onCreated,
  title = 'New model for this order',
  description = 'Created like any other model — the colors × sizes below become its variants, and the grid opens ready to order.',
}: {
  vendorId: string;
  taxonomy: Awaited<ReturnType<typeof api.products.taxonomy.query>> | null;
  onClose: () => void;
  onCreated: (productId: string) => void;
  /** Also used by receiving without an order (SCOPE §6.1), which says its own words. */
  title?: string;
  description?: string;
}) {
  const [name, setName] = useState('');
  const [styleNumber, setStyleNumber] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [season, setSeason] = useState('');
  const [colours, setColours] = useState('');
  const [sizes, setSizes] = useState('');
  /** Short, Regular, Long — only for a style that comes in lengths (2026-10-02). */
  const [lengths, setLengths] = useState('');
  const [cost, setCost] = useState('');
  const [msrp, setMsrp] = useState('');

  const colourList = colours
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  const sizeList = sizes
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  const lengthList = lengths
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  /** Colors × sizes × lengths: what pressing Create style makes. */
  const variantCount =
    Math.max(colourList.length, 1) * Math.max(sizeList.length, 1) * Math.max(lengthList.length, 1);
  const costCents = tryParseMoney(cost);
  const msrpCents = tryParseMoney(msrp);

  const create = useMutation({
    mutationFn: () => {
      const axes = {
        colours: colourList.length > 0 ? colourList : [null],
        sizes: sizeList.length > 0 ? sizeList : [null],
        lengths: lengthList.length > 0 ? lengthList : [null],
      };
      return api.products.create.mutate({
        styleNumber: styleNumber.trim(),
        name: name.trim(),
        vendorId,
        departmentId,
        categoryId: categoryId === '' ? null : categoryId,
        season: season.trim() === '' ? null : season.trim(),
        variants: axes.lengths.flatMap((length) =>
          axes.colours.flatMap((colour) =>
            axes.sizes.map((size) => ({
              colorName: colour,
              sizeLabel: size,
              altSize: length,
              costCents: costCents ?? 0,
              msrpCents,
              // Defaults to MSRP like the importer (§5.1-5); zero lands the
              // style in the finishing pass rather than silently unsellable.
              retailCents: msrpCents ?? 0,
            })),
          ),
        ),
      });
    },
    onSuccess: (result) => {
      recordBreadcrumb('purchasing', 'created a model mid-order');
      onCreated(result.productId);
    },
  });

  const problem =
    name.trim() === ''
      ? 'Give it a name'
      : styleNumber.trim() === ''
        ? 'Give it the vendor’s model number'
        : departmentId === ''
          ? 'Pick a category'
          : cost.trim() !== '' && costCents === null
            ? 'That cost is not a number'
            : msrp.trim() !== '' && msrpCents === null
              ? 'That MSRP is not a number'
              : undefined;

  return (
    <Modal
      open
      wide
      title={title}
      description={description}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={create.isPending}
          disabledReason={problem}
          onClick={() => create.mutate()}
        >
          Create model
          {colourList.length > 0 || sizeList.length > 0 || lengthList.length > 0
            ? ` · ${variantCount} variants`
            : ''}
        </Button>
      }
    >
      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
      <div className="style-form">
        <div className="field">
          <label className="field__label" htmlFor="order-style-name">
            Name
          </label>
          <input
            id="order-style-name"
            className="input"
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-number">
            Model number
          </label>
          <input
            id="order-style-number"
            className="input code"
            value={styleNumber}
            onChange={(event) => setStyleNumber(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-department">
            Category
          </label>
          <select
            id="order-style-department"
            className="select"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Pick a category</option>
            {(taxonomy?.departments ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.path}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-gender">
            Gender
          </label>
          <select
            id="order-style-gender"
            className="select"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">None</option>
            {(taxonomy?.genders ?? []).map((gender) => (
              <option key={gender.id} value={gender.id}>
                {`${gender.name}${gender.group === null ? '' : ` · ${gender.group}`}`}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-season">
            Season
          </label>
          <input
            id="order-style-season"
            className="input"
            placeholder="FA26"
            value={season}
            onChange={(event) => setSeason(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-colours">
            Colors, comma-separated
          </label>
          <input
            id="order-style-colours"
            className="input"
            placeholder="Black, Alpine Sage"
            value={colours}
            onChange={(event) => setColours(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-sizes">
            Sizes, comma-separated
          </label>
          <input
            id="order-style-sizes"
            className="input"
            placeholder="S, M, L, XL"
            value={sizes}
            onChange={(event) => setSizes(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-lengths">
            Lengths, comma-separated
          </label>
          <input
            id="order-style-lengths"
            className="input"
            placeholder="Short, Regular, Long"
            aria-describedby="order-style-lengths-hint"
            value={lengths}
            onChange={(event) => setLengths(event.target.value)}
          />
          <p className="field__hint" id="order-style-lengths-hint">
            Only for a model that comes in lengths. Each is its own grid.
          </p>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-cost">
            Unit cost
          </label>
          <input
            id="order-style-cost"
            className="input num"
            inputMode="decimal"
            placeholder="62.00"
            value={cost}
            onChange={(event) => setCost(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="order-style-msrp">
            MSRP
          </label>
          <input
            id="order-style-msrp"
            className="input num"
            inputMode="decimal"
            placeholder="129.95"
            value={msrp}
            onChange={(event) => setMsrp(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * The grid (SCOPE §5.2, and the client's "by - grid or sku").
 *
 * Colours down, sizes across, a quantity in each cell — the layout of every
 * vendor order form there is, which is the whole reason the client asked for
 * it. A variant that does not exist has no cell at all rather than a disabled
 * one (§2.2-13).
 */
function OrderGrid({
  detail,
  quantityFor,
  costFor,
  onQuantity,
  onFill,
  onAdded,
}: {
  detail: Awaited<ReturnType<typeof api.products.get.query>>;
  quantityFor: (variantId: string) => number;
  /** The draft's cost for a variant, falling back to its weighted average. */
  costFor: (variantId: string, fallback: number) => number;
  onQuantity: (variant: OrderableVariant, quantity: number) => void;
  onFill: (variants: readonly OrderableVariant[]) => void;
  /** A colour or size the style does not have yet, added without leaving. */
  onAdded: (updated: Awaited<ReturnType<typeof api.products.get.query>>) => void;
}) {
  const [addingAxes, setAddingAxes] = useState(false);
  const orderable: OrderableVariant[] = detail.variants
    .filter((variant) => variant.active)
    .map((variant, rank) => ({
      sizeSort: variant.sizeSort,
      rank,
      variantId: variant.id,
      productId: detail.product.id,
      productName: detail.product.name,
      sku: variant.sku,
      colorName: variant.colorName,
      sizeLabel: variant.sizeLabel,
      altSize: variant.altSize,
      costCents: variant.costCents,
      targetQty: variant.targetQty,
      onHand: variant.stock.onHand,
      onOrder: variant.stock.onOrder,
    }));

  // One grid per length: keyed on color and size alone, a style's Short,
  // Regular and Long shared one cell and two of them could not be ordered.
  const grids = sizeGrids(orderable);

  const suggestion = orderable.reduce((sum, variant) => sum + suggestedQuantity(variant), 0);

  return (
    <>
      <div className="order__gridhead">
        <p className="text-secondary">
          {detail.product.name} · <Mono>{detail.product.styleNumber}</Mono>
        </p>
        <div className="order__gridhead-actions">
          {/*
            The axes are editable from here (item 20): the grid is built from
            the variants that exist, and the moment a buyer finds the vendor
            now does a colour the style lacks is mid-order, not in Products.
            Same dialog the style editor uses — one implementation.
          */}
          <Button variant="ghost" onClick={() => setAddingAxes(true)}>
            Colors &amp; sizes
          </Button>
          {/* One press for a whole size run, not one per size (§2.2-5). */}
          <Button
            variant="secondary"
            disabledReason={
              suggestion === 0
                ? 'Nothing is below its target, or no targets are set on this model'
                : undefined
            }
            onClick={() => onFill(orderable)}
          >
            Fill to target · {suggestion} units
          </Button>
        </div>
      </div>

      {addingAxes ? (
        <AddVariantsDialog
          productId={detail.product.id}
          existing={detail.variants}
          onClose={() => setAddingAxes(false)}
          onAdded={(updated) => {
            setAddingAxes(false);
            onAdded(updated);
          }}
        />
      ) : null}

      {grids.map((grid) => (
        <div className="table-scroll" key={grid.altSize ?? 'one'}>
          <table className="table matrix">
            {grid.label !== null ? (
              <caption className="matrix__caption">{grid.label}</caption>
            ) : null}
            <thead>
              <tr>
                <th />
                {grid.sizes.map((size, index) => (
                  <th key={size ?? `size-${index}`} className="num">
                    {size ?? 'One size'}
                  </th>
                ))}
                {/* Beside the row, where the eye is when it decides (item 24). */}
                <th className="num">Units</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row, rowIndex) => {
                const rowVariants = row.cells.filter(
                  (cell): cell is OrderableVariant => cell !== undefined,
                );
                const rowUnits = rowVariants.reduce(
                  (sum, variant) => sum + quantityFor(variant.variantId),
                  0,
                );
                const rowCostCents = rowVariants.reduce(
                  (sum, variant) =>
                    sum +
                    quantityFor(variant.variantId) * costFor(variant.variantId, variant.costCents),
                  0,
                );
                const which = [row.label, grid.altSize].filter(Boolean).join(' ');

                return (
                  <tr key={row.key}>
                    <th scope="row" className="matrix__row-head">
                      {row.label}
                    </th>
                    {row.cells.map((variant, index) => {
                      // No cell at all, rather than an input that cannot act.
                      if (variant === undefined) return <td key={index} />;
                      const size = grid.sizes[index] ?? null;
                      const suggested = suggestedQuantity(variant);

                      return (
                        <td key={variant.variantId} className="matrix__cell">
                          <input
                            className="input num order__qty"
                            inputMode="numeric"
                            aria-label={`${which} ${size ?? 'one size'} quantity`}
                            value={quantityFor(variant.variantId) || ''}
                            placeholder={suggested > 0 ? String(suggested) : '0'}
                            onChange={(event) => {
                              const digits = event.target.value.replace(/\D/g, '').slice(0, 5);
                              onQuantity(variant, digits === '' ? 0 : Number.parseInt(digits, 10));
                            }}
                          />
                          {/* Labelled on the cell, not in a paragraph below
                              (item 23): the number should say what it is. */}
                          <span className="order__onhand">
                            {variant.onHand} on hand
                            {variant.onOrder > 0 ? ` · +${variant.onOrder} on order` : ''}
                          </span>
                        </td>
                      );
                    })}
                    <td className="num">{rowUnits > 0 ? rowUnits : ''}</td>
                    <td className="num money">{rowCostCents > 0 ? money(rowCostCents) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <p className="text-secondary">
        A grayed number in a box is the suggestion — target less on hand and on order. Unit costs
        are set under On this order: one price for the model, or size by size in its grid.
      </p>
    </>
  );
}

/**
 * One style on the order, drawn as the grid it was ordered in: each cell its
 * quantity and its unit cost, the style's units and cost beside its name.
 */
function OrderedStyle({
  style,
  onChange,
  onRemove,
  onCost,
  onStyleCost,
}: {
  style: OrderStyle;
  onChange: () => void;
  onRemove: () => void;
  onCost: (variantId: string, unitCostCents: number) => void;
  onStyleCost: (unitCostCents: number) => void;
}) {
  const grids = sizeGrids(style.lines);
  const costs = new Set(style.lines.map((line) => line.unitCostCents));
  /** The one price every size is at, or null while sizes differ. */
  const oneCost = costs.size === 1 ? [...costs][0]! : null;

  return (
    <section className="order__style" aria-label={`${style.productName} on this order`}>
      <div className="order__style-head">
        <div>
          <h3 className="order__style-name">{style.productName}</h3>
          <span className="text-secondary">
            {style.units} unit{style.units === 1 ? '' : 's'} · {money(style.costCents)} at cost
          </span>
        </div>
        <div className="order__style-actions">
          <label className="order__style-cost">
            <span className="text-secondary">Every size at</span>
            <CostInput
              value={oneCost}
              placeholder="Varies"
              label={`every size of ${style.productName}`}
              onChange={onStyleCost}
            />
          </label>
          {/* Opens this style's order grid above, and goes to it (2026-10-02):
              "what does Change quantities do?" — it changed the card above
              out of sight, and the press looked like nothing. */}
          <Button variant="ghost" onClick={onChange}>
            Edit quantities
          </Button>
          <Button variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>
      {grids.map((grid) => (
        <div className="table-scroll" key={grid.altSize ?? 'one'}>
          <table className="table matrix">
            {grid.label !== null ? (
              <caption className="matrix__caption">{grid.label}</caption>
            ) : null}
            <thead>
              <tr>
                <th />
                {grid.sizes.map((size, index) => (
                  <th key={size ?? `size-${index}`} className="num">
                    {size ?? 'One size'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row, rowIndex) => (
                <tr key={row.key}>
                  <th scope="row" className="matrix__row-head">
                    {row.label}
                  </th>
                  {row.cells.map((line, index) =>
                    line === undefined ? (
                      <td key={index} />
                    ) : (
                      <td key={line.variantId} className="matrix__cell order__line-cell">
                        <span className="order__line-qty">
                          {line.quantity}
                          {/* A length the grid does not split on still has to be seen. */}
                          {grid.altSize === null && line.altSize !== null
                            ? ` ${line.altSize}`
                            : ''}{' '}
                          ×
                        </span>
                        <CostInput
                          value={line.unitCostCents}
                          label={line.description}
                          onChange={(cents) => onCost(line.variantId, cents)}
                        />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

/** Cost in dollars, held as a draft so a half-typed figure is not parsed. */
function CostInput({
  value,
  label,
  onChange,
  placeholder,
}: {
  /** Null shows the box empty — "Every size at" while the sizes differ. */
  value: number | null;
  label: string;
  onChange: (cents: number) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value === null ? '' : (value / 100).toFixed(2));

  return (
    <input
      className="input num order__cost"
      inputMode="decimal"
      aria-label={`Unit cost for ${label}`}
      placeholder={placeholder}
      value={shown}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        // Commits on blur, never per keystroke — the same rule the receiving
        // bench learned the hard way (decision 28).
        const parsed = tryParseMoney(shown);
        if (parsed !== null) onChange(parsed);
        setDraft(null);
      }}
    />
  );
}
